import { Pin } from "lucide-react";

import { useSetCollectionPinMutation } from "@/api/api";
import { usePinnedCollections } from "@/api/hooks";
import { HGCollection } from "@/common/types";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "../tooltip";

/**
 * Star/pin toggle rendered inside a sidebar collection row. It lives inside the
 * row's <Link>, so clicks are stopped from navigating. Disabled (with a tooltip)
 * when pinning would exceed the per-user pinned-collection limit.
 */
export default function CollectionPinButton({
  collection,
  className,
}: {
  collection: HGCollection;
  className?: string;
}) {
  const [setPin] = useSetCollectionPinMutation();
  const { slots, atCap } = usePinnedCollections();

  const isPinned = collection.pin_position !== null;
  const disabled = !isPinned && atCap;

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    setPin({ collectionID: collection.id, isPinned: !isPinned });
  };

  const button = (
    <button
      type="button"
      aria-label={isPinned ? "Unpin collection" : "Pin collection"}
      aria-pressed={isPinned}
      aria-disabled={disabled}
      onClick={handleClick}
      className={cn(
        "flex aspect-square w-5 items-center justify-center rounded-md text-muted-foreground transition-colors hover:text-foreground [&>svg]:size-3.5",
        isPinned && "text-primary hover:text-primary",
        disabled && "cursor-not-allowed opacity-40 hover:text-muted-foreground",
        className,
      )}
    >
      <Pin strokeWidth={1.75} fill={isPinned ? "currentColor" : "none"} />
    </button>
  );

  if (disabled) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent side="right">
          {slots} of {slots} collections pinned
        </TooltipContent>
      </Tooltip>
    );
  }

  return button;
}
